import { useCallback, useEffect, useState } from 'react'
import { fetchWithAuth } from '../api/client'

export type AuthedObjectUrlState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; objectUrl: string }
  | { status: 'error'; httpStatus: number | null }

/**
 * Loads a protected file with the session's bearer token and exposes it as a
 * `blob:` URL an <img>/<iframe> can show (spec 2026-10-11-31). Unlike a bare
 * `src` pointing at the API, which authenticates by cookie and shows whatever
 * the server answers, a failed load (any non-2xx, after one refresh-and-retry
 * on a 401) becomes the `error` state: an error body is never rendered as the
 * document. The blob URL is revoked when the URL changes or on unmount.
 *
 * `mimeType` types a blob the server sent without a Content-Type, so the
 * browser's PDF viewer still recognises it.
 */
export function useAuthedObjectUrl(url: string | null, mimeType?: string | null) {
  const [state, setState] = useState<AuthedObjectUrlState>(url ? { status: 'loading' } : { status: 'idle' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!url) {
      setState({ status: 'idle' })
      return
    }
    let cancelled = false
    let created: string | null = null
    setState({ status: 'loading' })

    fetchWithAuth(url)
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) setState({ status: 'error', httpStatus: res.status })
          return
        }
        let blob = await res.blob()
        if (cancelled) return
        if (!blob.type && mimeType) blob = new Blob([blob], { type: mimeType })
        created = URL.createObjectURL(blob)
        setState({ status: 'ready', objectUrl: created })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', httpStatus: null })
      })

    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [url, mimeType, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { state, retry }
}
